import {
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { convertFormFieldType, responsesSatisfyConditions, buildOrderedFieldList } from "../utils";
import type { NextFieldLogicOptions } from "../utils/types";
import { useCallback, useMemo, useState } from "react";
import { FormTypesWithValues, TellescopeFieldResponse } from "../types";
import { useTranslation } from "react-i18next";
import { FORM_FIELD_VALIDATORS_BY_TYPE } from "@tellescope/validation";
import {
  AnswerForType,
  FormField,
  FormFieldOptions,
  FormResponseValue,
  FormResponseValueAnswer,
} from "@tellescope/types-models";
import { useQueryClient } from "@tanstack/react-query";

const FIELDS_WITH_VALIDATION_ON_CHANGE: (keyof AnswerForType)[] = [
  "Dropdown",
  "multiple_choice",
  "file",
  "files",
  "rating",
  "ranking",
  "Medications",
  "Timezone",
  "Related Contacts",
  "date",
  "dateString",
];

const getValueForValidation = <T extends keyof AnswerForType>(type: T, value: AnswerForType[T]) => {
  switch (type) {
    case "signature": {
      const typedValue = value as AnswerForType["signature"];
      return {
        type: "signature",
        fullName: typedValue?.fullName ?? "",
        signed: !!typedValue?.signed,
      };
    }
    case "date": {
      const typedValue = value as AnswerForType["date"];
      return typedValue?.toISOString() ?? "";
    }
    default:
      return value;
  }
};

const isValueEmpty = (value: unknown): boolean => {
  if (typeof value !== "number" && !value) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (Array.isArray(value) && value.every(isValueEmpty)) return true;
  if (value instanceof Date) return false;
  if (typeof value === "object" && Object.values(value).every(isValueEmpty)) return true;
  return false;
};

export type LocalTellescopeFormResponseMap = Record<string, TellescopeFieldResponse>;

export interface UseTellescopeFormArgs {
  formId: string;
  formTitle?: string;
  responseId?: string;
  enduserId: string;
  parentResponseId?: string;
  rootResponseId?: string;
}

export interface UseTellescopeFormReturn {
  currentResponses: LocalTellescopeFormResponseMap;
  validateField: <T extends keyof AnswerForType = keyof AnswerForType>(args: {
    fieldId: string;
    valueOverride?: AnswerForType[T];
  }) => Promise<string | null>;
  validateAllFields: () => Promise<boolean>;
  visibleFields: (FormField & { answer: TellescopeFieldResponse["answer"] })[];
  setFieldValue: <T extends FormTypesWithValues>(fieldId: string, value: AnswerForType[T]) => void;
  saveResponse: () => Promise<void>;
  submitForm: () => Promise<unknown>;
  submitFormMutation: ReturnType<typeof useTellescopeMutation<"form_responses", "submit_form_response">>;
  form: TellescopeModelFromName<"forms"> | undefined;
  fieldErrors: Record<string, string | null>;
  touchedFields: Set<string>;
  handleFieldBlur: (fieldId: string) => void;
  getFieldProps: <T extends FormTypesWithValues = FormTypesWithValues>(
    fieldId: string,
    type: T
  ) => {
    id: string;
    type: T;
    title: string;
    value: AnswerForType[T];
    onChange: (newValue: AnswerForType[T]) => void;
    onBlur?: () => void;
    error: string | null;
    touched: boolean;
    options?: FormFieldOptions;
  } | null;
  enduser: TellescopeModelFromName<"endusers"> | undefined;
  isComplete: boolean;
  isSubmitted: boolean;
}

export const useTellescopeForm = ({
  formId,
  formTitle: providedFormTitle,
  responseId,
  enduserId,
  parentResponseId,
  rootResponseId,
}: UseTellescopeFormArgs): UseTellescopeFormReturn => {
  const { t } = useTranslation("forms");
  const queryClient = useQueryClient();

  const formQuery = useTellescopeQuery({
    model: "forms",
    method: "getOne",
    request: formId,
    enabled: !providedFormTitle && !!formId,
  });

  const formTitle = providedFormTitle || formQuery.data?.title;

  const formFieldsQuery = useTellescopeInfiniteQuery({
    model: "form_fields",
    method: "getSome",
    request: {
      filter: {
        formId,
      },
    },
    select: (data) => ({
      fieldsArr: data.pages.flat(),
      fieldsMap: data.pages.reduce<Record<string, TellescopeModelFromName<"form_fields">>>((acc, curr) => {
        curr.forEach((field) => {
          acc[field.id] = field;
        });
        return acc;
      }, {}),
    }),
    enabled: !!formId,
  });

  const responseQuery = useTellescopeQuery({
    model: "form_responses",
    method: "getOne",
    request: responseId ?? "",
    enabled: !!responseId && !!formId,
  });
  const responsesMap = useMemo(
    () =>
      responseQuery.data?.responses?.reduce<Record<string, FormResponseValue>>((acc, curr) => {
        acc[curr.fieldId] = curr;
        return acc;
      }, {}),
    [responseQuery.data?.responses]
  );

  const enduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: enduserId,
  });

  const [unsavedResponses, setUnsavedResponses] = useState<LocalTellescopeFormResponseMap>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const currentResponses = useMemo(() => {
    return (
      formFieldsQuery.data?.fieldsArr.reduce<LocalTellescopeFormResponseMap>((acc, curr) => {
        const unsavedResponse = unsavedResponses[curr.id];
        if (unsavedResponse) {
          acc[curr.id] = unsavedResponse;
          return acc;
        }

        const savedResponse = responsesMap?.[curr.id];
        if (savedResponse) {
          const correctType = convertFormFieldType(savedResponse.answer.type);
          if (correctType === null) return acc;
          acc[curr.id] = savedResponse;
          return acc;
        }

        const correctType = convertFormFieldType(curr.type);
        if (correctType === null) return acc;

        // @ts-expect-error Union type gets in the way here
        const answer: FormResponseValueAnswer = {
          type: curr.type satisfies FormResponseValueAnswer["type"],
          value: undefined satisfies FormResponseValueAnswer["value"],
        };
        const emptyResponse: FormResponseValue = {
          fieldId: curr.id,
          fieldTitle: curr.title,
          answer,
        };
        acc[curr.id] = emptyResponse;
        return acc;
      }, {}) ?? {}
    );
  }, [formFieldsQuery.data?.fieldsArr, responsesMap, unsavedResponses]);

  const orderedFields = useMemo(() => {
    const responsesArray = Object.values(currentResponses);

    if (!responsesArray.length) return [];

    const options: NextFieldLogicOptions = {
      urlLogicValue: new URLSearchParams(window.location.search).get("logic") ?? undefined,
      dateOfBirth: enduserQuery.data?.dateOfBirth,
      gender: enduserQuery.data?.gender,
      state: enduserQuery.data?.state,
      activeResponses: responsesArray,
    };

    return buildOrderedFieldList(formFieldsQuery.data?.fieldsArr ?? [], responsesArray, options);
  }, [
    currentResponses,
    enduserQuery.data?.dateOfBirth,
    enduserQuery.data?.gender,
    enduserQuery.data?.state,
    formFieldsQuery.data?.fieldsArr,
  ]);

  const visibleFields = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return orderedFields.reduce<(FormField & { answer: TellescopeFieldResponse["answer"] })[]>((acc, field) => {
      if (
        !field.groupShowCondition ||
        !Object.keys(field.groupShowCondition) ||
        responsesSatisfyConditions(Object.values(currentResponses), field.groupShowCondition, {
          urlLogicValue: searchParams.get("logic") ?? undefined,
          dateOfBirth: enduserQuery.data?.dateOfBirth,
          gender: enduserQuery.data?.gender,
          state: enduserQuery.data?.state,
          activeResponses: Object.values(currentResponses),
        })
      ) {
        acc.push({
          ...field,
          answer:
            currentResponses[field.id]?.answer ??
            ({
              type: field.type satisfies FormResponseValueAnswer["type"],
              value: undefined satisfies FormResponseValueAnswer["value"],
            } as FormResponseValueAnswer),
        });
      }
      return acc;
    }, []);
  }, [
    orderedFields,
    currentResponses,
    enduserQuery.data?.dateOfBirth,
    enduserQuery.data?.gender,
    enduserQuery.data?.state,
  ]);

  const validateField = useCallback(
    async <T extends keyof AnswerForType = keyof AnswerForType>(args: {
      fieldId: string;
      valueOverride?: AnswerForType[T];
    }): Promise<string | null> => {
      const fieldId = args.fieldId;
      const field = formFieldsQuery.data?.fieldsMap[fieldId];
      if (!field) {
        const errorMessage = t("Field not found");
        setFieldErrors((prev) => ({
          ...prev,
          [fieldId]: errorMessage,
        }));
        return errorMessage;
      }

      const type = convertFormFieldType(field.type);
      if (type === null) {
        setFieldErrors((prev) => ({
          ...prev,
          [fieldId]: null,
        }));
        return null;
      }

      const validator = FORM_FIELD_VALIDATORS_BY_TYPE[field.type];
      if (!validator) {
        setFieldErrors((prev) => ({
          ...prev,
          [fieldId]: null,
        }));
        return null;
      }

      const response = currentResponses[fieldId];
      const value = "valueOverride" in args ? args.valueOverride : response?.answer.value;
      const isEmpty = isValueEmpty(value);

      // If empty and optional, no error
      if (isEmpty && field.isOptional) {
        setFieldErrors((prev) => ({
          ...prev,
          [fieldId]: null,
        }));
        return null;
      }

      // If empty and required, show required error
      if (isEmpty && !field.isOptional) {
        const errorMessage = t("{{title}} is required", { title: field.title });
        setFieldErrors((prev) => ({
          ...prev,
          [fieldId]: errorMessage,
        }));
        return errorMessage;
      }

      try {
        // For multiple_choice and Dropdown, the value is already in storage format (string[])
        // The FORM_FIELD_VALIDATORS_BY_TYPE validators expect input format for these fields
        // So we skip the transformer validators for fields that are already in storage format
        if (field.type === "multiple_choice" || field.type === "Dropdown") {
          // Just validate that the options are valid
          const selectedOptions = Array.isArray(value) ? value : [];
          const availableOptions = field.options?.optionDetails?.map((opt) => opt.option) || [];

          // Check if all selected options are valid (exist in available options)
          const invalidOptions = selectedOptions.filter(
            (opt) => typeof opt === "string" && !availableOptions.includes(opt)
          );
          if (invalidOptions.length > 0) {
            const errorMessage = t("Invalid option selected");
            setFieldErrors((prev) => ({
              ...prev,
              [fieldId]: errorMessage,
            }));
            return errorMessage;
          }

          // Check radio constraint for multiple_choice
          if (field.options?.radio && selectedOptions.length > 1) {
            const errorMessage = t("Only 1 choice is allowed");
            setFieldErrors((prev) => ({
              ...prev,
              [fieldId]: errorMessage,
            }));
            return errorMessage;
          }

          setFieldErrors((prev) => ({
            ...prev,
            [fieldId]: null,
          }));
          return null;
        }

        // For other field types, use the validator
        const type = convertFormFieldType(field.type);
        if (!type) return null;
        await validator(getValueForValidation<keyof AnswerForType>(type, value), field.options, field.isOptional);

        // Validation passed - clear any existing error
        setFieldErrors((prev) => ({
          ...prev,
          [fieldId]: null,
        }));
        return null;
      } catch (error) {
        // Validation failed - store error message
        let errorMessage = "";
        if (typeof error === "object" && error !== null && "message" in error) {
          const message = error.message;
          if (typeof message === "string") {
            errorMessage = message;
          } else {
            errorMessage = String(message);
          }
        } else {
          errorMessage = String(error);
        }

        setFieldErrors((prev) => ({
          ...prev,
          [fieldId]: errorMessage,
        }));
        return errorMessage;
      }
    },
    [currentResponses, formFieldsQuery.data?.fieldsMap, t]
  );

  const setFieldValue = useCallback(
    <T extends FormTypesWithValues>(fieldId: string, value: AnswerForType[T]) => {
      setUnsavedResponses((prev) => {
        const field = formFieldsQuery.data?.fieldsMap[fieldId];
        const originalResponse = prev[fieldId] ?? {
          fieldId,
          fieldTitle: field?.title ?? "",
          answer: {
            type: field?.type,
            value: undefined,
          },
        };
        if (typeof value === typeof originalResponse?.answer.value || originalResponse?.answer.value === undefined) {
          const newResponse: FormResponseValue = {
            ...originalResponse,
            answer: {
              ...originalResponse?.answer,
              value,
            } as FormResponseValueAnswer,
          };
          return {
            ...prev,
            [fieldId]: newResponse,
          };
        }
        return prev;
      });
      touchedFields.add(fieldId);
    },
    [formFieldsQuery.data?.fieldsMap, touchedFields]
  );

  const handleFieldBlur = useCallback(
    (fieldId: string) => {
      // Mark field as touched
      setTouchedFields((prev) => new Set([...prev, fieldId]));

      // Validate on blur
      validateField({ fieldId });
    },
    [validateField]
  );

  const validateAllFields = useCallback(async (): Promise<boolean> => {
    const fieldIds = Object.keys(currentResponses);

    // Mark all fields as touched
    setTouchedFields(new Set(fieldIds));

    // Validate all fields in parallel
    const validationResults = await Promise.all(fieldIds.map((fieldId) => validateField({ fieldId })));

    // Return true if no errors
    return validationResults.every((error) => error === null);
  }, [currentResponses, validateField]);

  const prepareFormResponseMutation = useTellescopeMutation({
    model: "form_responses",
    method: "prepare_form_response",
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: responseQuery.queryKey,
        exact: true,
      });
    },
  });

  const updateResponseMutation = useTellescopeMutation({
    model: "form_responses",
    method: "updateOne",
    autoCacheUpdateStrategy: "update",
  });

  const submitFormMutation = useTellescopeMutation({
    model: "form_responses",
    method: "submit_form_response",
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: responseQuery.queryKey,
        exact: true,
      });
    },
    autoCacheUpdateStrategy: "update",
  });

  const saveResponse = useCallback(async () => {
    let response = responseQuery.data;

    if (!responseId && !prepareFormResponseMutation.data?.response.id) {
      const res = await prepareFormResponseMutation.mutateAsync({
        formId,
        enduserId,
        parentResponseId,
        rootResponseId,
        title: formTitle,
      });
      response = res.response;
    }

    if (!response) return;

    updateResponseMutation.mutateAsync({
      id: response.id,
      responses: Object.values(currentResponses),
      _options: {
        replaceObjectFields: true,
      },
    });
  }, [
    currentResponses,
    enduserId,
    formId,
    formTitle,
    parentResponseId,
    prepareFormResponseMutation,
    responseId,
    responseQuery.data,
    rootResponseId,
    updateResponseMutation,
  ]);

  const submitForm = useCallback(async () => {
    // First validate all fields
    const isValid = await validateAllFields();

    if (!isValid) {
      throw new Error(t("Please fix validation errors before submitting"));
    }

    let response = responseQuery.data;

    // Prepare response if it doesn't exist yet
    if (!responseId && !prepareFormResponseMutation.data?.response.id) {
      const res = await prepareFormResponseMutation.mutateAsync({
        formId,
        enduserId,
        parentResponseId,
        rootResponseId,
        title: formTitle,
      });
      response = res.response;
    }

    if (!response || !response.accessCode) {
      throw new Error(t("Failed to prepare form response"));
    }

    // Submit the form using the access code
    const result = await submitFormMutation.mutateAsync({
      accessCode: response.accessCode,
      responses: Object.values(currentResponses),
      markedAsSubmitted: true,
    });

    return result;
  }, [
    currentResponses,
    enduserId,
    formId,
    formTitle,
    parentResponseId,
    prepareFormResponseMutation,
    responseId,
    responseQuery.data,
    rootResponseId,
    submitFormMutation,
    t,
    validateAllFields,
  ]);

  const getFieldProps = useCallback(
    <T extends FormTypesWithValues = FormTypesWithValues>(
      fieldId: string,
      type: T
    ): {
      id: string;
      type: T;
      title: string;
      value: AnswerForType[T];
      onChange: (newValue: AnswerForType[T]) => void;
      onBlur?: () => void;
      error: string | null;
      touched: boolean;
      options?: FormFieldOptions;
    } | null => {
      // Find the field in currentResponses
      const fieldResponse = currentResponses[fieldId];

      // Find field metadata from visibleFields
      const fieldMetadata = visibleFields.find((f) => f.id === fieldId);

      if (!fieldResponse || !fieldMetadata) {
        console.warn(`Field ${fieldId} not found in form responses or metadata`);
        return null;
      }

      if (type !== fieldMetadata.type) {
        console.warn(`Field type does not match provided type`);
        return null;
      }

      const validatesOnChange = FIELDS_WITH_VALIDATION_ON_CHANGE.includes(type);

      const onChange = (newValue: AnswerForType[T]) => {
        setFieldValue(fieldId, newValue);
        if (validatesOnChange) {
          validateField({ fieldId, valueOverride: newValue });
        }
      };

      const onBlur = validatesOnChange ? undefined : () => handleFieldBlur(fieldId);

      const touched = touchedFields.has(fieldId);

      return {
        id: fieldId,
        type,
        title: fieldMetadata.title,
        value: fieldResponse.answer.value as AnswerForType[T],
        onChange,
        onBlur,
        error: touched ? fieldErrors[fieldId] ?? null : null,
        touched,
        options: fieldMetadata.options,
      };
    },
    [currentResponses, visibleFields, fieldErrors, touchedFields, setFieldValue, validateField, handleFieldBlur]
  );

  const isComplete = useMemo(() => {
    // Check if all visible required fields have values and no validation errors
    return visibleFields.every((field) => {
      const fieldMetadata = formFieldsQuery.data?.fieldsMap[field.id];
      if (!fieldMetadata) return false;

      // If field is optional, it doesn't block completion
      if (fieldMetadata.isOptional) return true;

      // Check if field has a value
      const response = currentResponses[field.id];
      if (!response) return false;

      const value = response.answer.value;
      const hasValue = !isValueEmpty(value);

      // Check if field has validation errors
      const hasError = fieldErrors[field.id] !== null && fieldErrors[field.id] !== undefined;

      return hasValue && !hasError;
    });
  }, [visibleFields, currentResponses, fieldErrors, formFieldsQuery.data?.fieldsMap]);

  const result = {
    currentResponses,
    validateField,
    validateAllFields,
    visibleFields,
    setFieldValue,
    saveResponse,
    submitForm,
    submitFormMutation,
    form: formQuery.data,
    fieldErrors,
    touchedFields,
    handleFieldBlur,
    getFieldProps,
    enduser: enduserQuery.data,
    isComplete,
    isSubmitted: !!responseQuery.data?.submittedAt,
  };

  return result;
};
